import { CheckCircle2 } from "lucide-react";

import { useTran } from "@/hooks";
import { Button } from "@/components/ui/button";

const SurveyTakeResult = ({
  surveyTitle,
  score,
  maxScore,
  answered,
  total,
  durationSeconds,
  submittedAt,
  isGuestMode,
  onClose,
  onRestart,
}) => {
  const { t } = useTran();
  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return t("N/A");
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes <= 0) return `${secs}s`;
    return `${minutes}m ${secs.toString().padStart(2, "0")}s`;
  };
  const submittedAtLabel = submittedAt
    ? new Date(submittedAt).toLocaleString()
    : null;
  return (
    <div className="h-full items-center justify-center bg-background p-6">
      <div className="flex flex-col items-center text-center">
        <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
          <CheckCircle2 className="h-10 w-10" />
        </div>
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
          {t("Survey submitted")}
        </p>
        <h2 className="mt-3 text-3xl font-semibold text-foreground">
          {t("Nicely done!")}
        </h2>
        <p className="mt-2 max-w-lg text-sm text-muted-foreground">
          {surveyTitle}
        </p>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-radius border border-border bg-card p-4 text-center shadow-sm">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {t("Score")}
          </p>
          <p className="mt-2 text-3xl font-bold text-primary">
            {score ?? t("N/A")}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {t("of")} {maxScore || t("N/A")} {t("Points")}
          </p>
        </div>
        <div className="rounded-radius border border-border bg-card p-4 text-center shadow-sm">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {t("Questions")}
          </p>
          <p className="mt-2 text-3xl font-bold text-foreground">
            {answered}/{total}
          </p>
          <p className="text-[11px] text-muted-foreground">{t("Answered")}</p>
        </div>
        <div className="rounded-radius border border-border bg-card p-4 text-center shadow-sm">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {t("Duration")}
          </p>
          <p className="mt-2 text-3xl font-bold text-foreground">
            {formatDuration(durationSeconds)}
          </p>
          <p className="text-[11px] text-muted-foreground">{t("Time spent")}</p>
        </div>
      </div>

      {submittedAtLabel ? (
        <div className="mt-6 rounded-radius border border-dashed border-border bg-muted/40 p-4 text-center text-xs text-muted-foreground">
          {t("Response saved on")} {submittedAtLabel}
        </div>
      ) : null}

      <div className="mt-8 flex flex-wrap justify-center gap-3">
        {isGuestMode ? (
          <Button onClick={onClose}>{t("Close")}</Button>
        ) : (
          <>
            <Button
              onClick={() => window.location.assign("/")}
              variant="outline"
              className="rounded-full"
            >
              {t("Back to Dashboard")}
            </Button>
            <Button onClick={onRestart}>{t("Take Another")}</Button>
            <Button onClick={onClose}>{t("Close")}</Button>
          </>
        )}
      </div>
    </div>
  );
};

export default SurveyTakeResult;
